import { useState, useEffect } from 'react';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Ticket, Plus, Trash2, Power, PowerOff, Copy, Check, Loader2, HelpCircle, X as XIcon } from 'lucide-react';
import { toast } from 'sonner';
import { formatApiError } from '../lib/api';

const DISCOUNT_OPTIONS = [50, 75, 80, 90];
const PLAN_OPTIONS = [
  { id: 'pay_per_case', label: 'Pay Per Case (₹99)' },
  { id: '3_months', label: 'Starter (₹2,999)' },
  { id: '6_months', label: 'Pro (₹5,500)' },
  { id: '1_year', label: 'Clinic (₹9,999)' },
];

export default function CouponsTab() {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [copiedCode, setCopiedCode] = useState(null);

  // Create form state
  const [code, setCode] = useState('');
  const [discount, setDiscount] = useState(50);
  const [plans, setPlans] = useState([]);
  const [maxUses, setMaxUses] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [note, setNote] = useState('');
  const [showHelp, setShowHelp] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/coupons');
      setCoupons(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || 'Failed to load coupons');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const resetForm = () => {
    setCode(''); setDiscount(50); setPlans([]); setMaxUses(''); setExpiresAt(''); setNote('');
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const payload = {
        discount_percent: discount,
        applicable_plans: plans,
        note: note || undefined,
      };
      if (code.trim()) payload.code = code.trim().toUpperCase();
      if (maxUses) payload.max_uses = parseInt(maxUses, 10);
      if (expiresAt) {
        const d = new Date(expiresAt);
        if (!isNaN(d.getTime())) payload.expires_at = d.toISOString();
      }
      const { data } = await api.post('/admin/coupons', payload);
      toast.success(`Coupon ${data.code} created successfully`);
      resetForm();
      setShowCreate(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || 'Failed to create coupon');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id, codeText) => {
    if (!window.confirm(`Delete coupon ${codeText}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/coupons/${id}`);
      toast.success('Coupon deleted');
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || 'Failed to delete');
    }
  };

  const handleToggle = async (id) => {
    try {
      const { data } = await api.post(`/admin/coupons/${id}/toggle`);
      toast.success(data.is_active ? 'Coupon activated' : 'Coupon deactivated');
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || 'Failed to toggle');
    }
  };

  const copyCode = (codeText) => {
    navigator.clipboard?.writeText(codeText).then(() => {
      setCopiedCode(codeText);
      toast.success(`Copied ${codeText}`);
      setTimeout(() => setCopiedCode(null), 1500);
    });
  };

  const togglePlan = (id) => {
    setPlans((prev) => prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]);
  };

  return (
    <div className="space-y-4" data-testid="coupons-tab">
      {/* Help / How it works panel */}
      {showHelp && (
        <Card className="border-amber-200 bg-amber-50/40 shadow-sm" data-testid="coupons-help-panel">
          <CardContent className="pt-5 pb-5 relative">
            <button
              onClick={() => setShowHelp(false)}
              className="absolute top-3 right-3 text-stone-400 hover:text-stone-700"
              data-testid="close-help-btn"
            >
              <XIcon className="w-4 h-4" />
            </button>
            <h3 className="font-heading font-semibold text-stone-900 mb-3 flex items-center gap-2">
              <HelpCircle className="w-4 h-4 text-amber-600" /> How Discount Coupons Work
            </h3>
            <div className="text-sm text-stone-700 space-y-3 leading-relaxed">
              <div>
                <p className="font-semibold text-stone-900">1. Who can redeem a coupon?</p>
                <p>Only NEW users — anyone whose account has no prior <em>paid</em> subscription. Existing paying customers cannot use coupons.</p>
              </div>
              <div>
                <p className="font-semibold text-stone-900">2. Where do users enter the code?</p>
                <p>On the <strong>Subscription page</strong> (<code className="text-xs bg-stone-100 px-1 rounded">/subscription</code>) there's a <em>"Have a coupon code?"</em> input above the pricing cards. They type the code, click <strong>Apply</strong>, and the prices on every plan card update live with the discount shown.</p>
              </div>
              <div>
                <p className="font-semibold text-stone-900">3. How does the discount actually flow?</p>
                <p>When the user clicks "Subscribe" on a plan, the coupon is sent to Razorpay along with the order — they pay the discounted amount directly. No manual reconciliation needed.</p>
              </div>
              <div>
                <p className="font-semibold text-stone-900">4. What discounts can I create?</p>
                <p><strong>Only 50%, 75%, 80%, or 90%</strong>. No other percentages allowed.</p>
              </div>
              <div>
                <p className="font-semibold text-stone-900">5. Can I limit a coupon?</p>
                <p>Yes — by <strong>plans</strong> (e.g., only Pro), <strong>max uses</strong> (e.g., 100 redemptions total), and <strong>expiry date</strong>. Leave fields blank for unlimited / no-expiry / all-plans.</p>
              </div>
              <div>
                <p className="font-semibold text-stone-900">6. Code format?</p>
                <p>Either type a custom code (4-24 alphanumeric chars, e.g. <code className="text-xs bg-stone-100 px-1 rounded font-mono">LAUNCH50</code>) or leave blank — the system auto-generates one like <code className="text-xs bg-stone-100 px-1 rounded font-mono">CB7K2M9P</code>.</p>
              </div>
              <div>
                <p className="font-semibold text-stone-900">7. How do I track redemptions?</p>
                <p>The <strong>Usage</strong> column shows live count (e.g., <code className="text-xs bg-stone-100 px-1 rounded font-mono">12 / 100</code>). Click the <strong>copy</strong> icon next to a code to share it on WhatsApp / Email.</p>
              </div>
              <div>
                <p className="font-semibold text-stone-900">8. Pause or delete a coupon?</p>
                <p>Click the <strong>power icon</strong> to deactivate (users will see "no longer active") or the <strong>trash icon</strong> to delete permanently. Past redemptions remain unaffected.</p>
              </div>
              <div className="pt-2 border-t border-amber-200">
                <p className="font-semibold text-stone-900">💡 Marketing tip:</p>
                <p>Different codes for different channels (e.g., <code className="text-xs bg-stone-100 px-1 rounded font-mono">WHATSAPP50</code>, <code className="text-xs bg-stone-100 px-1 rounded font-mono">DOCTOR75</code>, <code className="text-xs bg-stone-100 px-1 rounded font-mono">VIP90</code>) — so usage stats tell you which channel is converting best.</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="border-stone-200 shadow-sm">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base font-heading flex items-center gap-2">
                <Ticket className="w-4 h-4 text-[#1A4D2E]" /> Discount Coupons
              </CardTitle>
              <p className="text-xs text-stone-500 mt-1">
                Issue discount codes to new users. Coupons apply only to a user's first paid subscription.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={() => setShowHelp((s) => !s)}
                className="gap-1.5 text-stone-600"
                data-testid="show-help-btn"
              >
                <HelpCircle className="w-4 h-4" /> {showHelp ? 'Hide Help' : 'How it works'}
              </Button>
              <Button
                onClick={() => setShowCreate(true)}
                className="bg-[#1A4D2E] hover:bg-[#143B23] text-white gap-1.5"
                data-testid="create-coupon-btn"
              >
                <Plus className="w-4 h-4" /> New Coupon
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-2">{[1,2,3].map(i => <div key={i} className="h-12 bg-stone-100 rounded animate-pulse" />)}</div>
          ) : coupons.length === 0 ? (
            <div className="text-center py-12 text-sm text-stone-500" data-testid="no-coupons">
              <Ticket className="w-10 h-10 text-stone-300 mx-auto mb-3" />
              No coupons yet. Click "New Coupon" to create your first one.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Code</TableHead>
                  <TableHead>Discount</TableHead>
                  <TableHead>Applicable Plans</TableHead>
                  <TableHead>Usage</TableHead>
                  <TableHead>Expires</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {coupons.map((c) => (
                  <TableRow key={c.id} data-testid={`coupon-row-${c.code}`}>
                    <TableCell>
                      <button
                        onClick={() => copyCode(c.code)}
                        className="font-mono text-sm font-semibold text-[#1A4D2E] hover:underline flex items-center gap-1"
                        data-testid={`copy-code-${c.code}`}
                        title="Click to copy"
                      >
                        {c.code}
                        {copiedCode === c.code ? <Check className="w-3 h-3 text-green-600" /> : <Copy className="w-3 h-3 text-stone-400" />}
                      </button>
                      {c.note && <p className="text-[10px] text-stone-400 mt-0.5">{c.note}</p>}
                    </TableCell>
                    <TableCell>
                      <Badge className="bg-amber-500 text-white">{c.discount_percent}% OFF</Badge>
                    </TableCell>
                    <TableCell className="text-xs">
                      {c.applicable_plans?.length === 0 ? (
                        <span className="text-stone-500">All paid plans</span>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {c.applicable_plans.map((p) => (
                            <Badge key={p} variant="outline" className="text-[10px]">
                              {PLAN_OPTIONS.find(po => po.id === p)?.label?.split(' (')[0] || p}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-xs font-mono">
                      {c.used_count}{c.max_uses ? ` / ${c.max_uses}` : ' / ∞'}
                    </TableCell>
                    <TableCell className="text-xs text-stone-600">
                      {c.expires_at ? new Date(c.expires_at).toLocaleDateString() : <span className="text-stone-400">No expiry</span>}
                    </TableCell>
                    <TableCell>
                      {c.is_active ? (
                        <Badge className="bg-green-100 text-green-700 border-green-300">Active</Badge>
                      ) : (
                        <Badge variant="outline" className="text-stone-400">Inactive</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost" size="icon" className="h-8 w-8"
                        onClick={() => handleToggle(c.id)}
                        title={c.is_active ? 'Deactivate' : 'Activate'}
                        data-testid={`toggle-coupon-${c.code}`}
                      >
                        {c.is_active ? <PowerOff className="w-4 h-4 text-amber-600" /> : <Power className="w-4 h-4 text-green-600" />}
                      </Button>
                      <Button
                        variant="ghost" size="icon" className="h-8 w-8 text-[#8B2626]"
                        onClick={() => handleDelete(c.id, c.code)}
                        title="Delete coupon"
                        data-testid={`delete-coupon-${c.code}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create dialog */}
      <Dialog open={showCreate} onOpenChange={(o) => { setShowCreate(o); if (!o) resetForm(); }}>
        <DialogContent className="max-w-lg" data-testid="create-coupon-dialog">
          <DialogHeader>
            <DialogTitle>Create new discount coupon</DialogTitle>
            <DialogDescription>
              Coupons are restricted to NEW users only — they apply to a user's first paid subscription.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div>
              <Label htmlFor="code-input">Code <span className="text-stone-400 font-normal">(optional — leave empty to auto-generate)</span></Label>
              <Input
                id="code-input"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                placeholder="e.g. LAUNCH50"
                maxLength={24}
                className="mt-1 font-mono uppercase"
                data-testid="coupon-code-input"
              />
              <p className="text-[11px] text-stone-400 mt-1">4-24 alphanumeric characters</p>
            </div>

            <div>
              <Label>Discount %</Label>
              <div className="flex gap-2 mt-1">
                {DISCOUNT_OPTIONS.map((d) => (
                  <button
                    key={d}
                    type="button"
                    onClick={() => setDiscount(d)}
                    className={`flex-1 py-2 rounded border-2 text-sm font-mono font-semibold transition-colors ${
                      discount === d ? 'border-[#1A4D2E] bg-[#1A4D2E] text-white' : 'border-stone-200 text-stone-600 hover:border-stone-300'
                    }`}
                    data-testid={`discount-${d}-btn`}
                  >
                    {d}% OFF
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label>Applicable plans <span className="text-stone-400 font-normal">(empty = all paid plans)</span></Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {PLAN_OPTIONS.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => togglePlan(p.id)}
                    className={`px-3 py-1.5 rounded-md border text-xs font-medium transition-colors ${
                      plans.includes(p.id) ? 'border-[#1A4D2E] bg-[#1A4D2E]/10 text-[#1A4D2E]' : 'border-stone-200 text-stone-600 hover:border-stone-300'
                    }`}
                    data-testid={`plan-toggle-${p.id}`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="max-uses">Max uses <span className="text-stone-400 font-normal">(blank = unlimited)</span></Label>
                <Input
                  id="max-uses" type="number" min="1"
                  value={maxUses} onChange={(e) => setMaxUses(e.target.value)}
                  placeholder="e.g. 100"
                  className="mt-1"
                  data-testid="max-uses-input"
                />
              </div>
              <div>
                <Label htmlFor="expires-at">Expires on <span className="text-stone-400 font-normal">(blank = never)</span></Label>
                <Input
                  id="expires-at" type="date"
                  value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)}
                  className="mt-1"
                  data-testid="expires-at-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="note-input">Internal note <span className="text-stone-400 font-normal">(optional)</span></Label>
              <Input
                id="note-input"
                value={note} onChange={(e) => setNote(e.target.value)}
                placeholder="e.g. Influencer Dr. Sharma campaign"
                maxLength={200}
                className="mt-1"
                data-testid="coupon-note-input"
              />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="ghost" onClick={() => { setShowCreate(false); resetForm(); }}>Cancel</Button>
              <Button type="submit" disabled={creating} className="bg-[#1A4D2E] hover:bg-[#143B23] text-white gap-2" data-testid="submit-coupon-btn">
                {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                Create Coupon
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
